import React from "react";
import { Section, Reveal, Glass, Mono } from "@/components/network/NetworkAtoms";
import { Lock, FileCheck2, EyeOff, MessagesSquare, History, BellRing } from "lucide-react";

const PILLARS = [
  { Icon: Lock, title: "Your documents live in a vault, not an inbox",
    body: "Bank statements and accounts are uploaded straight into encrypted storage. Nothing important is left "
        + "sitting in an email chain, and every file is tied to your case rather than floating around." },
  { Icon: EyeOff, title: "Nobody buys your enquiry",
    body: "We are not a lead generator. Your details are shared only with funders considering your application, "
        + "and never sold on to anyone else — not to insurers, not to other brokers, not to anyone." },
  { Icon: History, title: "Every step is logged",
    body: "Who looked at your file, when, and what they said. It means nothing gets lost between people, and if "
        + "you ask us what's happening, the answer is on the screen in front of us." },
  { Icon: MessagesSquare, title: "One person, one thread",
    body: "You're not passed around a call centre. Messages, documents and decisions all sit in one place, with "
        + "the same named contact from first conversation to drawdown." },
  { Icon: BellRing, title: "Deadlines sit with the system, not your memory",
    body: "If a funder goes quiet or a document has been outstanding too long, the case escalates internally. "
        + "Transactions slip when nobody is watching the clock; here something always is." },
  { Icon: FileCheck2, title: "Nothing is submitted behind your back",
    body: "You'll know which funders are being approached before it happens. No scattergun applications, and no "
        + "surprise searches appearing on your file." },
];

const MARKET = [
  { value: "£68bn", label: "Lent to UK SMEs by banks in 2025", note: "British Business Bank / UK Finance" },
  { value: "60%", label: "Of that came from challenger and specialist lenders", note: "not the big four" },
  { value: "£11.6bn", label: "SME asset finance written in H1 2025 alone", note: "British Business Bank tracker" },
  { value: "45%", label: "Of smaller UK businesses used external finance", note: "and most only asked one lender" },
];

export default function TrustTech() {
  return (
    <>
      <Section
        testId="funding-trust"
        eyebrow="Looking after you"
        title="The unglamorous part, done properly"
        lede="Raising finance means handing over accounts, statements and plans. This is precisely how that
              information is handled once it reaches us."
      >
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.05}>
              <Glass className="h-full p-7" testId={`trust-${i}`}>
                <p.Icon className="size-5 text-[#7da9ff]" />
                <h3 className="mt-5 text-[16.5px] font-semibold text-white tracking-tight leading-snug">{p.title}</h3>
                <p className="mt-3 text-[14px] leading-relaxed text-slate-400">{p.body}</p>
              </Glass>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section testId="funding-market" tight>
        <Reveal>
          <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-7 sm:p-10">
            <Mono>Why a panel beats a single bank</Mono>
            <p className="mt-5 max-w-2xl text-[15.5px] leading-relaxed text-slate-300">
              Business lending in this country stopped being a high-street game some time ago. Most of the money
              now sits with funders outside the big four — an advantage if someone knows where to place the case,
              and a problem if you're knocking on one door at a time.
            </p>
            <dl className="mt-9 grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {MARKET.map((m) => (
                <div key={m.value}>
                  <dt className="font-cabinet text-[30px] sm:text-[36px] leading-none font-bold text-white tracking-[-0.035em]">
                    {m.value}
                  </dt>
                  <dd className="mt-3 text-[13px] leading-snug text-slate-300">{m.label}</dd>
                  <dd className="mt-1.5 text-[11px] text-slate-500">{m.note}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
